import React, { useState, useEffect } from 'react';
import { Layout, Card, Row, Col, Statistic, Table, Tabs, DatePicker, Select, Space, Input, Button, Popover } from 'antd';
import { BarChartOutlined, LineChartOutlined, PercentageOutlined } from '@ant-design/icons';
import axios from 'axios';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ResponsiveContainer, ComposedChart } from "recharts";

// Import Ant Design styles
import 'antd/dist/antd.css';
import moment from 'moment';
import { ApiRequest } from '../../../services/api/api';
import { baseUrl } from '../../../services/api/base-url';

const { Header, Content } = Layout;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const {Option} = Select

// Define interfaces
interface ChartDataItem {
  [key: string]: string | number;
}

// Define data structure interfaces
interface OutputData extends ChartDataItem {
    day: string;
    output: number;
    hours: number;
  }

interface MachineData extends ChartDataItem {
    name: string;
    units: number;
    hours: number;
    utilization: number;
}

interface ProductData {
  key: number;
  rank: number;
  product: string;
  percentage: string;
  quantity: string;
}
interface UtilizationData {
  machine: string;
  utilization: number;
}


const ProductionReportDashboard: React.FC = () => {
  // State variables
  // State variable for statistics
  const [stats, setStats] = useState<any>(null);
  const [machineRunningData, setMachineRunningData] = useState<MachineData[]>([]);
  const [machineUtilizationData, setMachineUtilizationData] = useState<UtilizationData[]>([]);
  const [outputData, setOutputData] = useState<OutputData[]>([]);  
  const [productionDateRange, setProductionDateRange] = useState<[moment.Moment, moment.Moment] | null>(null);
  const [machineDateRange, setMachineDateRange] = useState<[moment.Moment, moment.Moment] | null>(null);
  const [utilizationtDateRange, setUtilizationDateRange] = useState<[moment.Moment, moment.Moment] | null>(null);
  const [timeFilter, setTimeFilter] = useState<string>('week');
  const [topProduct, setTopProduct] = useState<ProductData[]>([]);
  const [productOptions, setProductOptions] = useState<{value: string, label: string}[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<number>();
  //   // Mock data for demonstration purposes

  // const mockOutputData: OutputData[] = [
  //   { day: 'Monday', output: 150000, hours: 30 },
  //   { day: 'Tuesday', output: 300000, hours: 65 },
  //   { day: 'Wednesday', output: 250000, hours: 40 },
  //   { day: 'Thursday', output: 200000, hours: 85 },
  //   { day: 'Friday', output: 290000, hours: 45 },
  //   { day: 'Saturday', output: 140000, hours: 30 },
  //   { day: 'Sunday', output: 310000, hours: 65 },
  // ];

  const ProductionOutputChart = () => {
    return (
      <ResponsiveContainer width="100%" height={420}>
        <ComposedChart data={outputData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="day" />
          <YAxis
            yAxisId="left"
            orientation="left"
            stroke="#32CD32"
            label={{ value: 'Units', angle: -90, position: 'insideLeft' }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            stroke="#ff7300"
            label={{ value: 'Hours', angle: -90, position: 'insideRight' }}
          />
          <Tooltip />
          <Legend />
          <Bar yAxisId="left" dataKey="output" fill="#32CD32" opacity={0.6} />
          <Line yAxisId="right" type="monotone" dataKey="hours" stroke="#ff7300" dot={{ r: 4 }} />
        </ComposedChart>
      </ResponsiveContainer>
    );
  };
  
  // const mockMachineDataInit = [
  //   { name: "Machine 1", units: 120000, hours: 50, utilization: 83 },
  //   { name: "Machine 2", units: 100000, hours: 45, utilization: 75 },
  //   { name: "Machine 3", units: 110000, hours: 48, utilization: 80 },
  //   { name: "Machine 4", units: 90000, hours: 40, utilization: 67 },
  //   { name: "Machine 5", units: 130000, hours: 55, utilization: 92 },
  //   { name: "Machine 6", units: 140000, hours: 60, utilization: 100 },
  //   { name: "Machine 7", units: 125000, hours: 52, utilization: 87 },
  //   { name: "Machine 8", units: 100000, hours: 38, utilization: 63 },
  //   { name: "Machine 9", units: 95000, hours: 35, utilization: 58 },
  //   { name: "Machine 10", units: 105000, hours: 42, utilization: 70 },
  //   { name: "Machine 11", units: 85000, hours: 30, utilization: 50 },
  //   { name: "Machine 12", units: 115000, hours: 50, utilization: 83 },
  //   { name: "Machine 13", units: 70000, hours: 25, utilization: 42 },
  //   { name: "Machine 14", units: 75000, hours: 28, utilization: 47 },
  // ];

  const MachineRunningChart = () => {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={machineRunningData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis
            yAxisId="left"
            orientation="left"
            stroke="#aac9f5"
            label={{ value: 'Units', angle: -90, position: 'insideLeft' }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            stroke="#ff7300"
            label={{ value: 'Hours', angle: -90, position: 'insideRight' }}
          />
          <Tooltip />
          <Legend />
          <Bar yAxisId="left" dataKey="units" fill="#aac9f5" opacity={0.6} />
          <Line yAxisId="right" type="monotone" dataKey="hours" stroke="#ff7300" dot={{ r: 4 }} />
        </ComposedChart>
      </ResponsiveContainer>
    );
  };  

  // const mockUtilizationDataInit = [
  //   { machine: "Machine 1", utilization: 83 },
  //   { machine: "Machine 2", utilization: 75 },
  //   { machine: "Machine 3", utilization: 80 },
  //   { machine: "Machine 4", utilization: 67 },
  //   { machine: "Machine 5", utilization: 92 },
  //   { machine: "Machine 6", utilization: 100 },
  //   { machine: "Machine 7", utilization: 87 },
  //   { machine: "Machine 8", utilization: 63 },
  //   { machine: "Machine 9", utilization: 58 },
  //   { machine: "Machine 10", utilization: 70 },
  //   { machine: "Machine 11", utilization: 50 },
  //   { machine: "Machine 12", utilization: 83 },
  //   { machine: "Machine 13", utilization: 42 },
  //   { machine: "Machine 14", utilization: 47 },
  // ];
  const MachineUtilizationChart = () => {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={machineUtilizationData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="machine" />
          <YAxis yAxisId="left" orientation="left" stroke="#0000FF" label={{ value: 'Utilization (%)', angle: -90, position: 'insideLeft' }} />
          <Tooltip />
          <Legend />
          <Bar yAxisId="left" dataKey="utilization" fill="#0000FF" opacity={0.6} />
        </ComposedChart>
      </ResponsiveContainer>
    );
  };
  const mockTopProducts: ProductData[] = Array.from({ length: 10 }, (_, index) => ({
    key: index + 1,
    rank: index + 1,
    // product: 'Customize Eco/100ml Single Wall Compostable Paper Cup',
    product: 'Customize Eco',
    percentage: '10%',
    quantity: '100,000',
  }));

  // Load data on component mount
  const fetchStats = () => {
    ApiRequest({
      url: `${baseUrl}SalesReport/GetStatisticOfOutput`,
      method: 'get',
    }).then(response => {
      const data = response.data.data;
      setStats(data);
      setTopProduct(data?.weekOutputByBaseProduct)
      resetProductOptions(data?.weekOutputByBaseProduct)
    }).catch(error => {
      console.error('API call failed:', error);
    });
  };

  const resetProductOptions = (products:any) => {
    setProductOptions([]);
    const options = products.map((item:any) => ({
      label: item.productCode,
      value: item.baseProductId,
    }));
    console.log('Product options reset with:', options);
    setProductOptions(options);
  };
  const fetchProductionOutputAndRunningTime = (startDate: Date, endDate: Date, type: "weekDay" | "date" | "month") => {
    const startDateStr = startDate.toISOString().slice(0, 10).replace(/-/g, '/');
    const endDateStr = endDate.toISOString().slice(0, 10).replace(/-/g, '/');
    ApiRequest({
      url: `${baseUrl}SalesReport/GetProductionOutputAndRunningTime?startDate=${startDateStr}&endDate=${endDateStr}&type=${type}`,
      method: 'get',
    }).then(response => {
      const data = response.data.data;
      setOutputData(data);
    }).catch(error => {
      console.error('API call failed:', error);
    });
  };
  const fetchMachineRunningTime = (startDate: Date, endDate: Date) => {
    const startDateStr = startDate.toISOString().slice(0, 10).replace(/-/g, '/');
    const endDateStr = endDate.toISOString().slice(0, 10).replace(/-/g, '/');
    ApiRequest({
      url: `${baseUrl}SalesReport/GetMachineRunningTime?startDate=${startDateStr}&endDate=${endDateStr}`,
      method: 'get',
    }).then(response => {
      const data = response.data.data;
      setMachineRunningData(data);
    }).catch(error => {
      console.error('API call failed:', error);
    });
  };
  const fetchMachineUtilization = (startDate: Date, endDate: Date) => {
    const startDateStr = startDate.toISOString().slice(0, 10).replace(/-/g, '/');
    const endDateStr = endDate.toISOString().slice(0, 10).replace(/-/g, '/');
    ApiRequest({
      url: `${baseUrl}SalesReport/GetMachineUtilization?startDate=${startDateStr}&endDate=${endDateStr}`,
      method: 'get',
    }).then(response => {
      const data = response.data.data;
      setMachineUtilizationData(data);
    }).catch(error => {
      console.error('API call failed:', error);
    });
  };

  useEffect(() => {
    const fetchInitialData = async () => {
        await fetchStats();

        // 使用 getDateRange 函数生成日期范围
        const currentDate = new Date();
        const { startDate, endDate } = getDateRange('week', currentDate); // 默认使用 'week' 作为 activeKey

        // 调用相关函数
        fetchProductionOutputAndRunningTime(startDate, endDate, 'weekDay');
        fetchMachineRunningTime(startDate, endDate);
        fetchMachineUtilization(startDate, endDate);
    };

    fetchInitialData();
  }, []);

  // Production table columns
  const topProductColumns = [
    {
      title: 'No',
      dataIndex: 'no',
      key: 'no',
      width: 20,
    },
    {
      title: 'Product Code',
      dataIndex: 'productCode',
      key: 'productCode',
      render: (text: string, record: any) => (
        <Popover
          content={
            <div>
              <p>{record?.baseProductName}</p>
            </div>
          }
          title={text}
        >
          <a>{text}</a>
        </Popover>
      ),
    },
    {
      title: 'Percentage',
      dataIndex: 'percentage',
      key: 'percentage',
      width: 30,
    },
    {
      title: 'Total',
      dataIndex: 'totalQuantity',
      key: 'totalQuantity',
      width: 30,
    },
  ];

  // Handle time filter change
  const handleTimeFilterChange = (value: string) => {
    setTimeFilter(value);
  };

  // Handle date range change
  const handleDateRangeChange = (
    dates: any,
    setDateRange: (dates: any) => void,
    fetchData: (startDate: Date, endDate: Date) => void
  ) => {
    if (!dates || dates.length !== 2) return;
  
    setDateRange(dates);
  
    const startDate = new Date(dates[0]);
    startDate.setDate(startDate.getDate() + 1); // 增加 1 天
  
    const endDate = new Date(dates[1]);
    endDate.setDate(endDate.getDate() + 1); // 增加 1 天
  
    fetchData(startDate, endDate);
  };
  
  // 具体的调用方式
  const handleProductionDateRangeChange = (dates: any) => 
    handleDateRangeChange(dates, setProductionDateRange, (start, end) => fetchProductionOutputAndRunningTime(start, end, "date"));
  
  const handleMachineDateRangeChange = (dates: any) => 
    handleDateRangeChange(dates, setMachineDateRange, fetchMachineRunningTime);
  
  const handleUtilizationDateRangeChange = (dates: any) => 
    handleDateRangeChange(dates, setUtilizationDateRange, fetchMachineUtilization);
  
  
  
  const getDateRange = (activeKey: string, currentDate: Date): { startDate: Date, endDate: Date, type: "weekDay" | "date" | "month" } => {
    const startDate = new Date(currentDate);
    const endDate = new Date(currentDate);
    endDate.setDate(endDate.getDate() - 1);

    let type: "weekDay" | "date" | "month" = "weekDay";

    switch (activeKey) {
        case 'week':
            startDate.setDate(endDate.getDate() - 6);
            type = "weekDay";
            break;
        case 'month':
            startDate.setMonth(endDate.getMonth() - 1);
            type = "date";
            break;
        case 'year':
            // 设置开始日期为去年同月的1号
            startDate.setFullYear(currentDate.getFullYear() - 1);
            startDate.setDate(1); // 设置为月初1号

            // 设置结束日期为今年的上个月最后一天
            endDate.setMonth(currentDate.getMonth()); // 设置为当前月
            endDate.setDate(0); // 设置为上个月的最后一天（0表示上个月的最后一天）
            type = "month";
            break;
        default:
            break;
    }
    return { startDate, endDate, type };
  }

  const activeOutputTabChange = (activeKey: string) => {
      const currentDate = new Date(); // 当前日期
      const { startDate, endDate, type } = getDateRange(activeKey, currentDate);

      setOutputData([]);
      fetchProductionOutputAndRunningTime(startDate, endDate, type);
  };
  const activeMachineRunningTimeTabChange = (activeKey: string) => {
    const currentDate = new Date(); // 
    const { startDate, endDate, type } = getDateRange(activeKey, currentDate);

    setMachineRunningData([]);
    fetchMachineRunningTime(startDate, endDate);
  };
  const activeMachineUtilizationTabChange = (activeKey: string) => {
    const currentDate = new Date();
    const { startDate, endDate } = getDateRange(activeKey, currentDate);
    setMachineUtilizationData([]);
    fetchMachineUtilization(startDate, endDate);
  };
  const activeTop10TabChange = (key: string) => {
    // debugger
    switch (key) {
      case 'week':
        setTopProduct(stats?.weekOutputByBaseProduct)
        resetProductOptions(stats?.weekOutputByBaseProduct)
        break;
      case 'month':
        setTopProduct(stats?.monthOutputByBaseProduct)
        resetProductOptions(stats?.monthOutputByBaseProduct)
        break;
      case 'year':
        setTopProduct(stats?.yearOutputByBaseProduct)
        resetProductOptions(stats?.yearOutputByBaseProduct)
        break;
      default:
        break;
    }
  };
  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Content style={{ margin: '16px', background: '#f0f2f5', padding: '16px' }}>
        <Row gutter={16} style={{ marginBottom: '16px' }}>
          <Col span={12}>
            <Row>
              <Col span={8}>
                <Card style={{ height: 160, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Statistic
                    title={<b>Week Total Output</b>}
                    value={stats?.weekTotalOutput !== undefined ? stats?.weekTotalOutput : 'Loading...'}
                    valueStyle={{ color: '#3f8600' }}
                    prefix={<b><BarChartOutlined /></b>}
                  />
                </Card>
              </Col>
              <Col span={8}>
              <Card style={{ height: 160, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Statistic
                    title={<b>Month Total Output</b>}
                    value={stats?.monthTotalOutput !== undefined ? stats?.monthTotalOutput : 'Loading...'}
                    valueStyle={{ color: '#3f8600' }}
                    prefix={<b><BarChartOutlined /></b>}
                  />
                </Card>
              </Col>
              <Col span={8}>
              <Card style={{ height: 160, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Statistic
                    title={<b>Year Total Output</b>}
                    value={stats?.yearTotalOutput !== undefined ? stats?.yearTotalOutput : 'Loading...'}
                    valueStyle={{ color: '#3f8600' }}
                    prefix={<b><BarChartOutlined /></b>}
                  />
                </Card>
              </Col>
            </Row>
          </Col>
          <Col span={4}>
          <Card style={{ height: 160, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <Row style={{ width: '100%', textAlign: 'center' }}>
              <Col span={24}>
                <Statistic
                  title={<b>Average Annual Utilization</b>}
                  value={stats?.averageUtilization !== undefined ? stats?.averageUtilization : 0}
                  valueStyle={{ color: '#3f8600' }}
                  suffix="%"
                />
              </Col>
            </Row>
            <Row style={{ width: '100%', textAlign: 'center', marginTop: '0px' }}>
              <Col span={24}>
                <Statistic
                  title={<b>Average Annual Defect Reject Rate</b>}
                  value={stats?.averageRejectQuantity !== undefined ? stats?.averageRejectQuantity : 0}
                  valueStyle={{ color: '#cf1322' }}
                  suffix="%"
                />
              </Col>
            </Row>
          </Card>

          </Col>
          <Col span={8}>
            <Card style={{ height: 160 }}>
    
              <Space>
                <Row>
                <span style={{fontWeight: 'bold', marginLeft: '10px',marginRight: '10px'}}>Search Output By Base Product: </span>
                  <Col span={12}>
                  <Select
                    showSearch
                    style={{ width: 250 }}
                    placeholder="Select a product"
                    optionFilterProp="label"
                    filterOption={(input, option) => {
                      if (option === undefined) return false;
                      return option?.label.toLowerCase().includes(input.toLowerCase());
                    }}
                    options={productOptions}
                    value={selectedProductId}
                    onChange={(value) => {
                      console.log('selectedProductId changed to:', value);
                      setSelectedProductId(value);
                    }}/>
                  </Col> 
               
                </Row>
              </Space>
          
              <Table 
                  columns={topProductColumns} 
                  dataSource={topProduct ? topProduct.filter((item: any) => {
                    const isSelected = item.baseProductId === selectedProductId;
                    console.log('Filtering:', item, 'Selected:', isSelected);
                    return isSelected;
                  }) : []}
                  pagination={false}
                  size="small"
                  style={{ fontSize: '12px' }}
                />
            </Card>
          </Col>
        </Row>

        {/* Production Output Chart */}
        <Row gutter={16} style={{ marginBottom: '16px' }}>
          <Col span={16}>
          <Card
            title={
              <Space>
                Production Output / Running Time
                <DatePicker.RangePicker
                  style={{ width: 300 }}
                  defaultValue={[moment().startOf('week').subtract(7, 'days'), moment()]}
                  onChange={handleProductionDateRangeChange}
                />
              </Space>
            }
            style={{ marginBottom: '16px' }}
            bodyStyle={{ height: 540 }}
          >
            <Tabs defaultActiveKey="week" onChange={(key) => activeOutputTabChange(key)}>
              <TabPane tab="Week" key="week">
                <ProductionOutputChart/>
              </TabPane>
              <TabPane tab="Month" key="month">
                <ProductionOutputChart/>
              </TabPane>
              <TabPane tab="Year" key="year">
                <ProductionOutputChart/>
              </TabPane>
            </Tabs>
            <div style={{ fontSize: '14px', color: '#999', marginTop: '8px' }}>
              <b>* The efficiency of production capacity increases as the two values in the chart get closer.</b>
            </div>
          </Card>
        </Col>
        <Col span={8}>
          <Card title="Top 10 Output" bodyStyle={{ height: 540 }}>
          <Tabs defaultActiveKey="week"  onChange={(key) => activeTop10TabChange(key)}>
                <TabPane tab="Week" key="week">
                  <Table 
                    columns={topProductColumns} 
                    dataSource={topProduct?.slice(0, 10)}
                    pagination={false}
                    size="small"
                  />
                </TabPane>
                <TabPane tab="Month" key="month">
                  <Table 
                    columns={topProductColumns} 
                    dataSource={topProduct?.slice(0, 10)}
                    pagination={false}
                    size="small"
                  />
                </TabPane>
                <TabPane tab="Year" key="year">
                  <Table 
                    columns={topProductColumns} 
                    dataSource={topProduct?.slice(0, 10)}
                    pagination={false}
                    size="small"
                  />
                </TabPane>
              </Tabs>
            </Card>        
        </Col>
        </Row>
        {/* Machine Running Time Chart */}
        <Card title={
                <Space>
                  Machine Running Time
                  <DatePicker.RangePicker
                    style={{ width: 300 }}
                    defaultValue={[moment().startOf('week').subtract(7, 'days'), moment()]}
                    onChange={handleMachineDateRangeChange}
                  />
                </Space>
              } 
          style={{ marginBottom: '16px' }}>
          <Tabs defaultActiveKey="week" onChange={(key) => activeMachineRunningTimeTabChange(key)}>
            <TabPane tab="Week" key="week">
              <MachineRunningChart />
            </TabPane>
            <TabPane tab="Month" key="month">
              <div style={{ height: '300px', display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#999' }}>
                <MachineRunningChart />
              </div>
            </TabPane>
            <TabPane tab="Year" key="year">
              <div style={{ height: '300px', display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#999' }}>
                <MachineRunningChart />
              </div>
            </TabPane>
          </Tabs>
        </Card>

        {/* Machine Utilization Chart */}
        <Card 
          title={
            <Space>
              Machine Utilization
              <DatePicker.RangePicker
                style={{ width: 300 }}
                defaultValue={[moment().startOf('week').subtract(7, 'days'), moment()]}
                onChange={handleUtilizationDateRangeChange}
              />
            </Space>
          }
          style={{ marginBottom: '16px' }}
        >
          <Tabs defaultActiveKey="week" onChange={(key) => activeMachineUtilizationTabChange(key)}>
            <TabPane tab="Week" key="week">
              <MachineUtilizationChart />
            </TabPane>
            <TabPane tab="Month" key="month">
              <MachineUtilizationChart />
            </TabPane>
            <TabPane tab="Year" key="year">
              <MachineUtilizationChart />
            </TabPane>
          </Tabs>
        </Card>
      </Content>
    </Layout>
  );
};

export default ProductionReportDashboard;